import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const LoadingScreen = () => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 40);

    // Phase transitions
    const phaseTimer1 = setTimeout(() => setPhase(1), 500);
    const phaseTimer2 = setTimeout(() => setPhase(2), 1200);
    const phaseTimer3 = setTimeout(() => setPhase(3), 1800);

    return () => {
      clearInterval(interval);
      clearTimeout(phaseTimer1);
      clearTimeout(phaseTimer2);
      clearTimeout(phaseTimer3);
    };
  }, []);

  return (
    <motion.div 
      className="fixed inset-0 bg-void flex flex-col items-center justify-center z-[9999] overflow-hidden"
      data-testid="loading-screen"
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Animated Grid Background */}
      <div className="absolute inset-0 bg-grid opacity-30" />
      
      {/* Gradient Orbs */}
      <motion.div
        className="absolute w-[600px] h-[600px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(243,100,88,0.15) 0%, transparent 70%)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute w-[400px] h-[400px] rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
        }}
        animate={{
          scale: [1.2, 1, 1.2],
          x: [0, -50, 0],
          y: [0, 30, 0],
        }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      />

      {/* Main Content */}
      <div className="relative z-10 text-center">
        {/* Logo Animation */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <img 
            src="/logo.png" 
            alt="Avi Cement" 
            className="h-14 md:h-20 mx-auto"
          />
        </motion.div>

        {/* Building Blocks Animation */}
        <div className="flex gap-2 justify-center mb-8">
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.div
              key={i}
              className="w-4 h-4 md:w-5 md:h-5"
              style={{ backgroundColor: i % 2 === 0 ? '#f36458' : '#3b82f6' }}
              initial={{ y: 50, opacity: 0, scaleY: 0 }}
              animate={{ 
                y: phase >= 1 ? 0 : 50, 
                opacity: phase >= 1 ? 1 : 0,
                scaleY: phase >= 1 ? 1 : 0 
              }}
              transition={{ 
                delay: i * 0.1,
                duration: 0.4,
                type: 'spring',
                stiffness: 200
              }}
            />
          ))}
        </div>

        {/* Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: phase >= 2 ? 1 : 0, y: phase >= 2 ? 0 : 20 }}
          transition={{ duration: 0.4 }}
        >
          <h1 className="font-display text-2xl md:text-3xl font-bold text-white mb-2">
            AVI CEMENT
          </h1>
          <p className="text-gray-500 font-mono text-sm">
            Building India's Future
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: phase >= 3 ? 1 : 0 }}
          className="mt-12 w-48 mx-auto"
        >
          <div className="h-1 bg-surface-light rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-coral to-electric"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
          <p className="text-center mt-3 font-mono text-xs text-gray-600">
            {progress}%
          </p>
        </motion.div>
      </div>

      {/* Bottom decorative line */}
      <motion.div
        className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-coral via-electric to-mint"
        initial={{ width: 0 }}
        animate={{ width: '100%' }}
        transition={{ duration: 2 }}
      />
    </motion.div>
  );
};

export default LoadingScreen;
